import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export const EXAM_FACT_FIELDS = [
  ["official_question_count", "Questions on the real exam", "number", "90"],
  ["time_limit_min", "Time limit (minutes)", "number", "90"],
  ["passing_score", "Passing score", "text", "750"],
  ["passing_scale", "Score scale (e.g. 900, or %)", "text", "900"],
  ["question_formats", "Question formats (comma-separated)", "text", "multiple-choice, performance-based"],
  ["objectives_version", "Objectives version / date", "text", "SY0-701 v1.0, Nov 2023"],
  ["udemy_category", "Udemy category", "text", "IT & Software"],
  ["udemy_subcategory", "Udemy subcategory", "text", "IT Certifications"],
  ["udemy_primary_topic", "Udemy primary topic", "text", "CompTIA Security+"],
  ["career_field", "Career field", "text", "cybersecurity"],
  ["compliance_note", "Compliance note (optional)", "text", "DoD 8570/8140"],
  ["eligibility", "Eligibility or prerequisites (optional; renders How to qualify)", "text", "No prerequisites; two years of experience recommended"],
];
const OPTIONAL = new Set(["compliance_note", "eligibility"]);

export const emptyExamFacts = () => Object.fromEntries(EXAM_FACT_FIELDS.map(([k]) => [k, ""]));

export const examFactErrors = (facts) => {
  const errs = [];
  EXAM_FACT_FIELDS.forEach(([k, label, type]) => {
    const v = facts?.[k];
    const s = Array.isArray(v) ? v.join(", ") : String(v ?? "").trim();
    if (!s) { if (!OPTIONAL.has(k)) errs.push(`${label} is required`); }
    else if (type === "number" && !(+s > 0)) errs.push(`${label} must be a whole number greater than 0`);
  });
  return errs;
};

export const examFactsPayload = (facts) => {
  const out = {};
  EXAM_FACT_FIELDS.forEach(([k, , type]) => {
    const v = facts?.[k];
    const s = Array.isArray(v) ? v.join(", ") : String(v ?? "").trim();
    out[k] = type === "number" ? +s : s;
  });
  return out;
};

export function ExamFactsFields({ facts, onChange, prefix = "facts", errors = [] }) {
  return (
    <div data-testid={`${prefix}-exam-facts`}>
      <p className="text-xs font-semibold text-zinc-700">Exam facts <span className="text-red-600">*</span> <span className="font-normal text-zinc-500">— straight from the vendor's exam page and Udemy's category tree; they feed the Udemy intro lecture and spec sheet</span></p>
      <div className="grid grid-cols-3 gap-3 mt-2">
        {EXAM_FACT_FIELDS.map(([k, label, type, ph]) => (
          <label key={k} className="text-xs font-medium text-zinc-600">{label}
            <input data-testid={`${prefix}-${k}`} type={type} min={type === "number" ? 1 : undefined} placeholder={ph}
                   className={`w-full border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-sky-400 ${errors.length && !OPTIONAL.has(k) && !String(facts?.[k] ?? "").trim() ? "border-red-400" : "border-zinc-300"}`}
                   value={Array.isArray(facts?.[k]) ? facts[k].join(", ") : (facts?.[k] ?? "")}
                   onChange={(e) => onChange({ ...facts, [k]: e.target.value })} /></label>
        ))}
      </div>
      {errors.length > 0 && (
        <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid={`${prefix}-exam-facts-errors`}>{errors.join(" · ")}</p>
      )}
    </div>
  );
}

export function ExamFactsEditor({ id, cert, onSaved }) {
  const [facts, setFacts] = useState({ ...emptyExamFacts(), ...(cert.exam_facts || {}) });
  const [ident, setIdent] = useState({ exam_code: cert.exam_code || "", short_name: cert.short_name || "" });
  const errors = examFactErrors(facts);
  if (!ident.exam_code.trim()) errors.push("Exam code is required (the vendor's exam ID, e.g. SY0-701)");
  const save = async () => {
    if (errors.length) return toast.error(errors.join(" · "), { duration: 10000 });
    try {
      await api.patch(`/admin/certs/${id}`, { exam_facts: examFactsPayload(facts), exam_code: ident.exam_code.trim(),
                                              short_name: ident.short_name.trim() || null });
      toast.success("Exam facts saved — render the Udemy intro lecture from its card");
      onSaved();
    } catch (e) { toast.error(errMsg(e), { duration: 10000 }); }
  };
  const input = "w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-sky-400";
  return (
    <div className="w-full">
      <div className="grid grid-cols-3 gap-3 mb-3">
        <label className="text-xs font-medium text-zinc-600">Exam code <span className="text-red-600">*</span>
          <input data-testid="banner-facts-exam_code" className={`${input} ${!ident.exam_code.trim() ? "border-red-400" : ""}`} placeholder="SY0-701"
                 value={ident.exam_code} onChange={(e) => setIdent({ ...ident, exam_code: e.target.value })} /></label>
        <label className="text-xs font-medium text-zinc-600">Short name (titles, spec sheet)
          <input data-testid="banner-facts-short_name" className={input} placeholder="Security+"
                 value={ident.short_name} onChange={(e) => setIdent({ ...ident, short_name: e.target.value })} /></label>
      </div>
      <ExamFactsFields facts={facts} onChange={setFacts} prefix="banner-facts" />
      <button data-testid="banner-facts-save-btn" onClick={save} className="mt-2 px-3 py-1.5 rounded-md bg-sky-500 text-white text-xs font-semibold hover:bg-sky-600">Save exam facts</button>
    </div>
  );
}
